import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Truck, MapPin, Wallet, CheckCircle2, XCircle, ArrowRight,
  Package, Navigation, PauseCircle, PlayCircle, Save,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DELIVERY } from "@/constants/testIds";

function dollars(c) { return `$${((c || 0) / 100).toFixed(2)}`; }

const NEXT_TRANSITION = { assigned: "picked_up", picked_up: "in_transit", in_transit: "delivered" };
const NEXT_LABEL      = { assigned: "Pick up", picked_up: "Start transit", in_transit: "Mark delivered" };

export default function DeliveryDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState({});

  const reload = async () => {
    try { const { data } = await api.get("/delivery/me"); setData(data); setProfile(data.profile); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const toggleAvail = async () => {
    const next = data.profile.availability === "on_shift" ? "off_shift" : "on_shift";
    await api.post("/delivery/me/availability", { availability: next });
    await reload();
  };
  const accept = async (id) => {
    setBusy(true); setErr("");
    try { await api.post(`/delivery/me/orders/${id}/accept`); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const transition = async (id, status) => {
    setBusy(true); setErr("");
    try { await api.post(`/delivery/me/orders/${id}/transition`, { status }); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const saveProfile = async () => {
    setBusy(true);
    try { await api.put("/delivery/me/profile", {
      vehicle_make: profile.vehicle_make, vehicle_model: profile.vehicle_model,
      vehicle_plate: profile.vehicle_plate, phone: profile.phone,
      bio: profile.bio, zones_served: profile.zones_served || [],
    }); await reload(); }
    finally { setBusy(false); }
  };

  if (err) return <div className="text-rose-300">{err}</div>;
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING DRIVER DECK…</div>;

  const cfg = data.delivery_config;
  const configured = cfg.base_fee_cents != null && cfg.per_mile_cents != null;
  const active = data.active;
  const totals = data.earnings_totals;

  return (
    <div data-testid={DELIVERY.driverDashboard} className="space-y-10">
      {/* Section 4 — also expose generic data-testid for tooling */}
      <span data-testid="driver-dashboard" hidden />
      <header className="fade-up">
        <div className="eyebrow flex items-center gap-2"><Truck className="h-3 w-3" /> Driver console</div>
        <h1 className="headline-xl">{user?.name?.split(" ")[0]}'s deck</h1>
        <p className="text-lifted-dim mt-2 max-w-2xl text-sm">
          Accept orders, run the route, get paid. Bg-check: <span className="text-lifted-text">{data.profile.background_check}</span>.
        </p>
        <div className="mt-5 flex items-center gap-3">
          <button data-testid={DELIVERY.availabilityToggle} onClick={toggleAvail} className="btn-lifted !text-[0.7rem]">
            {data.profile.availability === "on_shift" ? <><PauseCircle className="h-3 w-3" /> Go off-shift</> : <><PlayCircle className="h-3 w-3" /> Go on-shift</>}
          </button>
          <span className="chip">{data.profile.availability.replace("_", " ")}</span>
          {!configured && <span className="chip chip--purple !text-[0.6rem]">Pay rates not configured · earnings pending_review · $0</span>}
        </div>
      </header>

      {/* Earnings summary */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Completed" value={data.profile.stats.completed_deliveries} />
        <Stat label="Lifetime earnings" value={dollars(data.profile.stats.lifetime_earnings_cents)} />
        <Stat label="Pending review" value={dollars(totals.pending_review)} />
        <Stat label="Approved (unpaid)" value={dollars(totals.approved)} />
      </section>

      {/* Active assignment */}
      {active && (
        <section data-testid={DELIVERY.activeCard} className="glass-panel p-6 fade-up border-lifted-green/30">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow !text-lifted-green flex items-center gap-2"><Navigation className="h-3 w-3" /> Active delivery</div>
              <div className="mt-2 font-anton text-3xl uppercase">{active.source} · {active.order_id}</div>
              <p className="text-sm text-lifted-dim mt-1">{active.pickup_address} → {active.dropoff_address}</p>
              <p className="text-xs text-lifted-mute mt-1">{active.miles} mi · zone <span className="text-lifted-text">{active.zone_key || "—"}</span></p>
              <span className="chip chip--green mt-3 inline-block">{active.status.replace("_", " ")}</span>
              {/* Section 4 — itemized list with variant titles so the driver knows exactly what they're carrying */}
              {Array.isArray(active.items) && active.items.length > 0 && (
                <div className="mt-3" data-testid="delivery-active-items">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim mb-1">
                    Cargo
                  </div>
                  <ul className="text-xs text-white space-y-0.5">
                    {active.items.map((it, i) => (
                      <li key={i} className="font-mono" data-testid={`delivery-item-${i}`}>
                        · {it.qty || 1}× {it.title || it.handle}
                        {it.variant_title && (
                          <span className="text-lifted-green"> — {it.variant_title}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {NEXT_TRANSITION[active.status] && (
                <button data-testid={DELIVERY.transitionBtn(NEXT_TRANSITION[active.status])} onClick={() => transition(active.id, NEXT_TRANSITION[active.status])} disabled={busy} className="btn-lifted !text-[0.7rem]">
                  {NEXT_LABEL[active.status]} <ArrowRight className="h-3 w-3" />
                </button>
              )}
              <button data-testid={DELIVERY.transitionBtn("failed")} onClick={() => transition(active.id, "failed")} disabled={busy} className="btn-ghost !text-[0.7rem] hover:!text-rose-300">
                <XCircle className="h-3 w-3" /> Failed
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Available */}
      {!active && (
        <section data-testid={DELIVERY.availableList} className="glass-panel p-6">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Package className="h-3 w-3" /> Available orders</div>
          {data.available.length === 0
            ? <div className="text-lifted-mute text-sm mt-3">Nothing in your zones right now. Try expanding zones or wait for the next batch.</div>
            : <ul className="mt-4 divide-y divide-white/5">
                {data.available.map((o) => (
                  <li key={o.id} data-testid={DELIVERY.availableRow(o.id)} className="py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-bebas tracking-[0.16em] text-lifted-text truncate">{o.pickup_address} → {o.dropoff_address}</div>
                      <div className="text-xs text-lifted-dim">{o.source} · {o.order_id} · {o.miles} mi · zone {o.zone_key || "—"}</div>
                    </div>
                    <button data-testid={DELIVERY.acceptBtn(o.id)} onClick={() => accept(o.id)} disabled={busy} className="btn-lifted !text-[0.7rem]">
                      Accept <ArrowRight className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>}
        </section>
      )}

      {/* Earnings ledger */}
      <section data-testid={DELIVERY.earningsList} className="glass-panel p-6">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Wallet className="h-3 w-3" /> Earnings ledger</div>
        <table className="w-full mt-4 text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="py-2">When</th><th>Source</th><th>Miles</th><th>Amount</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.earnings.slice(0, 25).map((e) => (
              <tr key={e.id} data-testid={DELIVERY.earningsRow(e.id)} className="border-b border-white/5">
                <td className="py-2 text-xs text-lifted-dim">{e.created_at?.slice(0,16).replace("T"," ")}</td>
                <td><span className="chip">{e.order_source}</span></td>
                <td className="text-lifted-text">{e.miles}</td>
                <td className="text-lifted-green">{dollars(e.amount_cents)}</td>
                <td>{{
                  pending_review: <span className="chip">Pending</span>,
                  approved: <span className="chip chip--green">Approved</span>,
                  paid: <span className="chip chip--purple">Paid</span>,
                  void: <span className="chip">Void</span>,
                }[e.status]}</td>
              </tr>
            ))}
            {data.earnings.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-lifted-mute">No deliveries yet.</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Profile editor */}
      <section className="glass-panel p-6 fade-up">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><MapPin className="h-3 w-3" /> Driver profile</div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Vehicle make" value={profile.vehicle_make || ""} onChange={(v) => setProfile({ ...profile, vehicle_make: v })} />
          <Field label="Model"        value={profile.vehicle_model || ""} onChange={(v) => setProfile({ ...profile, vehicle_model: v })} />
          <Field label="Plate"        value={profile.vehicle_plate || ""} onChange={(v) => setProfile({ ...profile, vehicle_plate: v })} />
          <Field label="Phone"        value={profile.phone || ""} onChange={(v) => setProfile({ ...profile, phone: v })} />
        </div>
        <div className="mt-3">
          <label className="lifted-label">Zones served (comma-separated keys, e.g. downtown,north)</label>
          <input className="input-lifted" value={(profile.zones_served || []).join(",")} onChange={(e) => setProfile({ ...profile, zones_served: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
        </div>
        <button data-testid={DELIVERY.profileSave} onClick={saveProfile} disabled={busy} className="btn-lifted mt-4 !text-[0.7rem]">
          <Save className="h-3 w-3" /> Save profile
        </button>
      </section>

      <Link to="/training/driver" className="btn-ghost inline-flex">→ Sit your Lifted Driver certification</Link>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="glass-panel p-4">
      <div className="eyebrow !text-lifted-text-dim">{label}</div>
      <div className="mt-1 font-anton text-2xl text-lifted-text">{value}</div>
    </div>
  );
}
function Field({ label, value, onChange }) {
  return (
    <div>
      <label className="lifted-label">{label}</label>
      <input className="input-lifted" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
